import type { AdCompliancePagesParams } from "@common/api/aircraft"
import { z } from "zod"
import type { ColumnSizing } from "@/lib/column-widths"
import { regulatorSchema } from "@/routes/_layout/airworthiness-directives/-lib/search"
import {
  type Direction,
  directionSchema,
  withFixed,
} from "@/routes/_layout/maintenance-programmes_/$programmeId/-lib/table"

export const AD_COLUMNS = [
  "number",
  "applicable",
  "description",
  "due",
  "requirements",
  "recurrent",
  "authority",
  "accepted",
  "accepted_by",
] as const

export const adColumnSchema = z.enum(AD_COLUMNS)

export type AdColumn = z.infer<typeof adColumnSchema>

export const AD_COLUMN_LABELS: Record<AdColumn, string> = {
  number: "AD no.",
  applicable: "Applicable",
  description: "Description",
  due: "Due date",
  requirements: "Requirements",
  recurrent: "Recurrent",
  authority: "Issued by",
  accepted: "Accepted",
  accepted_by: "Accepted by",
}

export const AD_SIZING: ColumnSizing<AdColumn> = {
  id: "aircraft-ad-compliance",
  widths: {
    number: 9,
    applicable: 5.5,
    description: 18,
    due: 8,
    requirements: 16,
    recurrent: 5.5,
    authority: 6,
    accepted: 7,
    accepted_by: 8,
  },
  fill: "description",
  extras: [],
  dropOrder: [
    "accepted_by",
    "requirements",
    "authority",
    "due",
    "recurrent",
    "applicable",
    "accepted",
  ],
}

export const adSearchFields = {
  ad_search: z.string().optional().catch(undefined),
  ad_direction: directionSchema.optional().catch(undefined),
  ad_columns: z.array(adColumnSchema).optional().catch(undefined),
  ad_recurrent: z.boolean().optional().catch(undefined),
  ad_applicable: z.boolean().optional().catch(undefined),
  ad_authority: z.array(regulatorSchema).optional().catch(undefined),
  ad_item: z.string().optional().catch(undefined),
}

const adSearchSchema = z.object(adSearchFields)

export type AdSearch = z.infer<typeof adSearchSchema>

export type AdFilterValues = Pick<
  AdSearch,
  "ad_recurrent" | "ad_applicable" | "ad_authority"
>

export const NO_AD_FILTERS: AdFilterValues = {
  ad_recurrent: undefined,
  ad_applicable: undefined,
  ad_authority: undefined,
}

export function hasAdFilters(values: AdFilterValues): boolean {
  return (
    values.ad_recurrent !== undefined ||
    values.ad_applicable !== undefined ||
    Boolean(values.ad_authority?.length)
  )
}

export type AdDisplay = {
  columns: AdColumn[]
  direction: Direction
}

export function adDisplayOf(search: AdSearch): AdDisplay {
  return {
    columns: search.ad_columns
      ? withFixed(AD_COLUMNS, "number", search.ad_columns)
      : [...AD_COLUMNS],
    direction: search.ad_direction ?? "desc",
  }
}

export function nextAdDirection(
  display: AdDisplay,
): Pick<AdSearch, "ad_direction"> {
  return { ad_direction: display.direction === "desc" ? "asc" : undefined }
}

export function adCompliancePagesParams(
  aircraftId: string,
  search: AdSearch,
): AdCompliancePagesParams {
  return {
    aircraft_id: aircraftId,
    search: search.ad_search,
    recurrent: search.ad_recurrent,
    applicability: search.ad_applicable,
    regulatory_authority: search.ad_authority,
    order_direction: adDisplayOf(search).direction,
  }
}

export function yesNo(flag: "Y" | "N"): string {
  return flag === "Y" ? "Yes" : "No"
}
